import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import * as authApi from '../api/auth'

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!token) {
      setError('Missing reset token. Please use the link from your email.')
      return
    }

    setLoading(true)
    try {
      await authApi.resetPassword({ token, password })
      setSuccess(true)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg || 'Unable to reset your password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <>
        <Helmet>
          <title>Reset Password - ARPU</title>
        </Helmet>
        <div className="max-w-md mx-auto mt-12 text-center">
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-6">
            <p className="mb-4">Invalid password reset link. Please request a new one.</p>
            <Link
              to="/forgot-password"
              className="inline-block px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
            >
              Request Reset Link
            </Link>
          </div>
        </div>
      </>
    )
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Password Reset - ARPU</title>
        </Helmet>
        <div className="max-w-md mx-auto mt-12 text-center">
          <div className="bg-success/10 border border-success/30 rounded-lg p-8">
            <h1 className="text-2xl font-heading font-bold text-success mb-3">Password Reset</h1>
            <p className="text-text-light mb-6">
              Your password has been updated. You can now log in with your new password.
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - ARPU</title>
      </Helmet>

      <div className="max-w-md mx-auto mt-12">
        <h1 className="text-2xl font-heading font-bold text-text mb-2 text-center">
          Set New Password
        </h1>
        <p className="text-text-light text-center mb-6">
          Enter your new password below.
        </p>

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <p className="text-xs text-text-muted mt-1">Minimum 8 characters</p>
          </div>

          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-text mb-1">
              Confirm Password
            </label>
            <input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-text-muted">
          <Link to="/login" className="text-primary hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    </>
  )
}
